import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import Icon from "../../assets/icons/Icon";
import SvgIcon from "../../shared/Components/UI/Svg";
import SubHeading from "../../shared/Components/UI/SubHeading";
import showToast from "../../utils/toastUtil";
import {
  deleteAxios,
  getAxios,
  postAxios,
} from "../../shared/Services/commonService";
import {
  API_MFA_DISABLE,
  API_MFA_ENABLE,
  API_MFA_SETUP,
} from "../../utils/api/ApiConstant";
import { QrCode } from "../../shared/Components";

const APP_LINKS = {
  googleAndroind:
    "https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2",
  googleIos: "https://apps.apple.com/us/app/google-authenticator/id388497605",
  microsoftAndroid:
    "https://play.google.com/store/apps/details?id=com.azure.authenticator",
  microsoftIos:
    "https://apps.apple.com/us/app/microsoft-authenticator/id983156458",
};

const getRecoveryCodes = (recoveryCodes) =>
  recoveryCodes
    ?.split(",")
    .map((code) => code.trim())
    .filter(Boolean) || [];

const TwoFactorAuth = () => {
  const userName = useSelector((state) => state.user?.user?.userName);
  const [otp, setOtp] = useState(new Array(6).fill(""));
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [isTextCopied, setIsTextCopied] = useState({
    secreteKey: false,
    recoveryCode: false,
  });
  const [mfaResult, setMfaResult] = useState({});

  const inputRefs = useRef([]);
  const recoveryCodeList = getRecoveryCodes(mfaResult?.recoveryCodes);
  console.log("🚀 ~ TwoFactorAuth ~ recoveryCodeList:", recoveryCodeList);

  const fetch2FASecret = async () => {
    try {
      const response = await getAxios(API_MFA_SETUP);

      if (response) {
        setMfaResult(response);
      }
    } catch (err) {
      console.error("Error fetching 2FA secret:", err);
    }
  };

  useEffect(() => {
    fetch2FASecret();
  }, []);

  const handleChange = (element, index) => {
    const value = element.value;

    if (!/^\d*$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    setError("");

    if (value && index < otp.length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    const pasteData = e.clipboardData.getData("text").trim();

    if (!/^\d{6}$/.test(pasteData)) return;

    const newOtp = pasteData.split("");
    setOtp(newOtp);
    setError("");
    inputRefs.current[otp.length - 1]?.focus();
  };

  const handleCopy = async (text, action, successMessage = "Copied!") => {
    if (!text || isTextCopied[action]) return;

    try {
      await navigator.clipboard.writeText(text);
      showToast.success(successMessage);

      setIsTextCopied((prev) => ({ ...prev, [action]: true }));
      setTimeout(() => {
        setIsTextCopied((prev) => ({ ...prev, [action]: false }));
      }, 3000);
    } catch (err) {
      showToast.error("Unable to copy");
    }
  };

  const handleSubmit = async () => {
    const finalCode = otp.join("");

    if (finalCode.length !== 6) {
      setError("Please enter code");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const payload = {
        code: finalCode,
      };

      if (!mfaResult?.mfaEnabled) {
        payload.mfaType = mfaResult?.mfaType;
        payload.secretKey = mfaResult?.secretKey;
      }

      // const URL = !mfaResult?.mfaEnabled ? API_MFA_ENABLE : API_MFA_DISABLE;

      let result;
      {
        !mfaResult?.mfaEnabled
          ? (result = await postAxios(API_MFA_ENABLE, payload))
          : (result = await deleteAxios(API_MFA_DISABLE, payload));
      }

      setOtp(new Array(6).fill(""));
      inputRefs.current[0]?.focus();

      if (result?.success) {
        fetch2FASecret();
      } else {
        setError(result?.message || "Invalid code. Please try again.");
      }
    } catch (err) {
      setError(err?.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  const openInNewTab = (key) => {
    const url = APP_LINKS[key];

    if (url) {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <>
      <SubHeading eyebrow="Home" subTitle="Two Factor Authentication" />

      <div className="twoFa-container">
        <div className="row m-0 p-0">
          <div className="col-lg-7 col-12">
            <div className="twoFa-left">
              <div className="status-box">
                <span
                  className={`security_icon ${
                    mfaResult?.mfaEnabled ? "success" : "danger"
                  }`}
                >
                  <SvgIcon name="Security" />
                </span>

                <div className="status_box">
                  <h3>2FA Security Status</h3>
                  <p
                    className={`status ${mfaResult?.mfaEnabled ? "success" : "danger"}`}
                  >
                    {mfaResult?.mfaEnabled
                      ? "Enabled and Activated"
                      : "Currently Disabled"}
                  </p>
                </div>
              </div>

              <div className="step">
                <h4>1. Install an Authenticator App</h4>
                <p>Install one of these authentication apps:</p>

                <div className="app-buttons">
                  <div className="app">
                    <span>
                      <SvgIcon name="GoogleAo" className="icon_app" />
                      Google Authenticator
                    </span>
                    <button onClick={() => openInNewTab("googleIos")}>
                      iOS
                    </button>
                    <button onClick={() => openInNewTab("googleAndroind")}>
                      Android
                    </button>
                  </div>

                  <div className="app">
                    <span>
                      <SvgIcon name="MicrosoftAo" className="icon_app" />
                      Microsoft Authenticator
                    </span>
                    <button onClick={() => openInNewTab("microsoftIos")}>
                      iOS
                    </button>
                    <button onClick={() => openInNewTab("microsoftAndroid")}>
                      Android
                    </button>
                  </div>
                </div>
              </div>

              <div className="step">
                <h4>2. Setup Secret Key</h4>
                <p>Your secret key for manual entry:</p>

                <div className="secret-box">
                  <input
                    type="text"
                    readOnly
                    value={mfaResult?.secretKey || ""}
                  />
                  <button
                    className="copy-btn"
                    onClick={() =>
                      handleCopy(mfaResult?.secretKey, "secreteKey")
                    }
                  >
                    {isTextCopied.secreteKey ? (
                      <Icon name="Check" />
                    ) : (
                      <Icon name="Copy" />
                    )}
                  </button>
                </div>

                <p className="pt-2">Recovery codes:</p>
                <div className="secret-box secret-box--codes">
                  <div className="recovery-codes">
                    {recoveryCodeList.length > 0 ? (
                      recoveryCodeList.map((code) => (
                        <span key={code} className="recovery-code">
                          {code}
                        </span>
                      ))
                    ) : (
                      <span className="recovery-code">No recovery codes</span>
                    )}
                  </div>

                  {recoveryCodeList.length > 0 && (
                    <button
                      className="copy-btn"
                      onClick={() =>
                        handleCopy(recoveryCodeList.join(","), "recoveryCode")
                      }
                    >
                      {isTextCopied.recoveryCode ? (
                        <Icon name="Check" />
                      ) : (
                        <Icon name="Copy" />
                      )}
                    </button>
                  )}
                </div>
              </div>

              <div className="twoFa-right mobile_towFa">
                <h4>Scan QR Code</h4>

                <div className="qr-box">
                  <QrCode
                    SecretKey={mfaResult?.secretKey}
                    UserName={userName}
                    qrCodeUrl={mfaResult?.qrCodeUrl}
                  />
                </div>
                <p>Scan this QR code with your authenticator app</p>
              </div>

              <div className="step">
                <h4>3. Verify Setup</h4>
                <p>Enter the 6-digit code from your app:</p>

                <div className="code-inputs" onPaste={handlePaste}>
                  {otp.map((digit, index) => (
                    <input
                      key={index}
                      type="text"
                      inputMode="numeric"
                      maxLength="1"
                      value={digit}
                      onChange={(e) => handleChange(e.target, index)}
                      onKeyDown={(e) => handleKeyDown(e, index)}
                      ref={(el) => {
                        inputRefs.current[index] = el;
                      }}
                      autoFocus={index === 0}
                      className="otp-input"
                    />
                  ))}
                </div>

                {error && <p className="font-danger">{error}</p>}

                <button
                  className={`enable-btn ${
                    mfaResult?.mfaEnabled ? "danger" : "success"
                  }`}
                  onClick={handleSubmit}
                  disabled={loading}
                >
                  {loading
                    ? "Processing..."
                    : mfaResult?.mfaEnabled
                      ? "Disable 2FA"
                      : "Enable 2FA"}
                </button>

                {!mfaResult?.mfaEnabled && (
                  <p>
                    Note: Activating Two-Factor Authentication will log you out
                    of the Telegram bot. You will need to log in again once the
                    setup is complete.
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="col-lg-5 col-12 desktop_towFa">
            <div className="twoFa-right">
              <h4>Scan QR Code</h4>

              <div className="qr-box">
                <QrCode
                  SecretKey={mfaResult?.secretKey}
                  UserName={userName}
                  qrCodeUrl={mfaResult?.qrCodeUrl}
                />
              </div>

              <p>Scan this QR code with your authenticator app</p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default TwoFactorAuth;
